import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Immutable from 'immutable';
import type { ImmutableRootState, ImmutableEntity } from '@uncinc/react-drupal-core/types';
import classnames from 'classnames';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import {
  setOperationStatus,
  UI_OPERATION_STATUS_SUCCESS,
  UI_OPERATION_STATUS_ERROR,
} from '@uncinc/react-drupal-core';

import { createOrUpdateUserProfile } from '../../store/actions/Profile';
import InformationOverlay from '../InformationOverlay';
import { DialogConfirm } from '../DialogConfirm';
import { useDialogDrawerContext } from '../../context/DialogDrawerContext';
import AddressForm from '../AddressForm';
import AddressTeaser from './AddressTeaser';
import type { UserProfileAddress } from '../../types/user';

import './style.scss';

export interface AddressOverviewProps {
  selectionMode?: boolean,
  title?: string,
  activeIndex?: number,
  onSelect?: Function,
}

const AddressOverview: React.FC<AddressOverviewProps> = ({
  selectionMode = false,
  title,
  onSelect = () => { },
  activeIndex = null,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const {
    closeDialog,
  } = useDialogDrawerContext();
  const uid = useSelector<ImmutableRootState<number>, number>((state) => state.getIn(['login', 'uid'], 0));
  const userProfile = useSelector<ImmutableRootState<string>, string>((state) => state.getIn(['user', uid, 'profile'], undefined));
  const userProfileAddresses = useSelector<ImmutableRootState<Immutable.List<ImmutableEntity<UserProfileAddress>>>, Immutable.List<ImmutableEntity<UserProfileAddress>>>((state) => state.getIn(['user', uid, 'profile', 'address'], Immutable.List()));
  const defaultAddressIndex = useSelector<ImmutableRootState<string>, string>((state) => state.getIn(['user', uid, 'profile', 'field_default_address_index', 0, 'value'], undefined));

  let usableProfileAddresses = Immutable.List(userProfileAddresses);
  // When a profile is created a empty address is created, remove this from the list.
  if (usableProfileAddresses.getIn([0, 'address_line1']) === null) {
    usableProfileAddresses = usableProfileAddresses.delete(0);
  }

  const profileUpdateStatus = useSelector<ImmutableRootState<string>, string>((state) => state.getIn(['ui', 'profile', 'operations', 'update', 'status'], ''));

  const classNames = classnames('AddressOverview', {
    'AddressOverview--selection-mode': selectionMode,
  });

  // On address create.
  const onCreate = (address: any) => {
    const data: any = {
      address: usableProfileAddresses.insert(
        usableProfileAddresses.size,
        Immutable.fromJS(address) as ImmutableEntity<UserProfileAddress>,
      ).toJS(),
    };
    if (address.set_default) {
      data.field_default_address_index = [{ value: String(usableProfileAddresses.size) }];
    }
    dispatch(createOrUpdateUserProfile(data) as any);
  };

  // On address edit.
  const onEdit = (index: number, address: any) => {
    const data: any = {
      address: usableProfileAddresses.delete(index).insert(
        index,
        Immutable.fromJS(address) as ImmutableEntity<UserProfileAddress>,
      ).toJS(),
    };
    if (address.set_default) {
      data.field_default_address_index = [{ value: String(index) }];
    }
    // If is default, but not anymore
    if (Number(index) === Number(defaultAddressIndex) && !address.set_default) {
      data.field_default_address_index = null;
    }
    dispatch(createOrUpdateUserProfile(data) as any);
  };

  // On address destroy.
  const onDestroy = (index: number) => {
    const data: any = {
      address: usableProfileAddresses.delete(index).toJS(),
    };
    // Remove default.
    if (Number(index) === Number(defaultAddressIndex)) {
      data.field_default_address_index = null;
    }
    // Addresses can't be empty.
    if (data.address.length === 0) {
      data.address = [{
        country_code: 'NL',
      }];
    }

    dispatch(createOrUpdateUserProfile(data) as any);
  };

  // Listens to the update status and shows a success or error toast message.
  useEffect(() => {
    if (profileUpdateStatus !== null) {
      if (profileUpdateStatus === UI_OPERATION_STATUS_SUCCESS) {
        toast.success(t('mijn_vnl.overview.profile_success'));
      } else if (profileUpdateStatus === UI_OPERATION_STATUS_ERROR) {
        toast.error(t('mijn_vnl.overview.profile_error'));
      }
      dispatch(setOperationStatus('profile', 'update', null, null));
    }
  }, [profileUpdateStatus, dispatch, t]);

  return (
    <div className={classNames}>
      {title && (
        <h2>{title}</h2>
      )}
      {userProfile && (
        <div>
          {usableProfileAddresses.size === 0 && (
            <p>{t('mijn_vnl.addresses.empty')}</p>
          )}
          <div className="AddressOverview__address_add_action">
            <InformationOverlay
              overlayKey="user_add_addresses"
              buttonIcon="plus"
              buttonTitle={t('address.add_new')}
              buttonClassName="Button--primary"
            >
              <AddressForm
                onSubmit={onCreate}
                allowSetDefault={!selectionMode}
              />
            </InformationOverlay>
          </div>
          <div className="row">
            {usableProfileAddresses.map((
              userProfileAddress: ImmutableEntity<UserProfileAddress>,
              index,
            ) => (
              <div
                className="col-12"
              >
                <div className={`AddressOverview__address_item ${activeIndex === index && 'selected'}`}>
                  <div
                    role="button"
                    tabIndex={0}
                    className="Teaser"
                    onClick={() => {
                      if (selectionMode) {
                        onSelect(index);
                        closeDialog();
                      }
                    }}
                    onKeyDown={(e) => {
                      if (selectionMode && (e.key === 'Enter' || e.key === ' ')) {
                        onSelect(index);
                        closeDialog();
                      }
                    }}
                  >
                    {/* Render the Address Teaser. */}
                    <AddressTeaser
                      isDefault={index === Number(defaultAddressIndex)}
                      address={userProfileAddress}
                    />
                  </div>

                  {/* Address actions */}
                  <div className="AddressOverview__address_item__actions">
                    <div className="AddressOverview__address_item__actions__edit">
                      <InformationOverlay
                        overlayKey="user_edit_addresses"
                        buttonIcon="edit"
                        buttonTitle={t('general.edit')}
                      >
                        <AddressForm
                          address={userProfileAddress.toJS()}
                          isDefault={index === Number(defaultAddressIndex)}
                          allowSetDefault={!selectionMode}
                          onSubmit={(data: any) => {
                            onEdit(index, data);
                          }}
                        />
                      </InformationOverlay>
                    </div>

                    {!selectionMode && (
                      <div className="AddressOverview__address_item__actions__delete">
                        <DialogConfirm
                          buttonText={t('general.delete')}
                          buttonIcon="trash"
                          onSubmit={() => {
                            onDestroy(index);
                          }}
                        >
                          <div className="AddressOverview__address_item__actions__delete__item">
                            <h3>
                              {t('address.confirm_delete')}
                            </h3>
                            <AddressTeaser
                              address={userProfileAddress}
                            />
                          </div>
                        </DialogConfirm>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AddressOverview;
